import React, { useState, useEffect, useContext } from "react";
import { ServerConfigContext } from "../context/ServerConfigContext";
import { useJWTAuth } from "../context/JWTAuthContext";
import {
  boardApiService,
  useBoardApi,
  POST_SORT_OPTIONS,
  PAGINATION_DEFAULTS,
} from "../utils/boardApiService";
import { formatDateTime } from "../utils/dateFormat";
import "./BoardContent.css";

const BoardContent = ({
  currentBoard,
  onToggleSidebar,
  onPostClick,
  onWriteClick,
}) => {
  const serverConfigContext = useContext(ServerConfigContext);
  const { getServer, isConfigLoaded } = serverConfigContext;
  const {
    token: jwtToken,
    user: jwtUser,
    isAuthenticated: isJWTAuthenticated,
  } = useJWTAuth();
  const boardApi = useBoardApi(serverConfigContext);

  // Authorization 헤더 생성 (JWT 토큰 우선 사용)
  const getAuthHeaders = () => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };

    // JWT 토큰이 있으면 우선 사용, 없으면 기존 AuthContext 토큰 사용
    const authToken = jwtToken || (jwtUser && localStorage.getItem("auth_token"));

    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
      console.log("🔑 [BoardContent] Authorization 헤더 설정:", {
        tokenSource: jwtToken ? "JWT" : "AuthContext",
        tokenLength: authToken.length,
        tokenStart: authToken.substring(0, 20) + "...",
      });
    } else {
      console.warn("⚠️ [BoardContent] 인증 토큰이 없습니다.");
    }

    return headers;
  };
  const [boardData, setBoardData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSolved, setIsSolved] = useState(null);
  // 문자열 ID를 서버 숫자 ID로 매핑
  const getBoardId = (boardKey) => {
    const boardMapping = {
      free: 1,
      qna: 2,
      notice: 3,
      frontend: 4,
      backend: 5,
      mobile: 6,
      photo: 7,
      music: 8,
      game: 9,
    };
    return boardMapping[boardKey] || boardKey;
  };

  // 기본 게시판 데이터
  const defaultBoardData = {
    free: {
      title: "자유게시판",
      description: "자유롭게 이야기를 나누는 공간입니다.",
      icon: "💬",
    },
    qna: {
      title: "Q&A",
      description: "궁금한 것들을 질문하고 답변을 받아보세요.",
      icon: "❓",
    },
    frontend: {
      title: "프론트엔드",
      description: "프론트엔드 개발에 대한 모든 것을 다룹니다.",
      icon: "🎨",
    },
    backend: {
      title: "백엔드",
      description: "백엔드 개발과 서버 기술에 대해 논의합니다.",
      icon: "⚙️",
    },
    mobile: {
      title: "모바일",
      description: "모바일 앱 개발 정보를 공유합니다.",
      icon: "📱",
    },
  };

  // 게시판 정보 불러오기
  const fetchBoardInfo = async () => {
    try {
      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        setBoardData(defaultBoardData[currentBoard] || defaultBoardData.free);
        return;
      }

      const boardId = getBoardId(currentBoard);
      console.log("🚀 [BoardContent] 게시판 정보 조회:", {
        currentBoard,
        boardId,
      });

      // 프록시를 통한 API 호출 - boards 목록 API 사용
      const apiUrl = `/board-api/api/v1/boards`;
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(
          `API 호출 실패: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();
      if (result.success && result.data && Array.isArray(result.data)) {
        // data 배열의 각 카테고리에서 boards를 찾음
        let foundBoard = null;
        for (const category of result.data) {
          if (category.boards && Array.isArray(category.boards)) {
            foundBoard = category.boards.find(board => board.id === boardId);
            if (foundBoard) break;
          }
        }

        if (foundBoard) {
          setBoardData({
            title: foundBoard.name,
            description: foundBoard.description,
            icon: foundBoard.icon || "📝",
            id: foundBoard.id,
            min_read: foundBoard.min_read_grade || null,
            min_write: foundBoard.min_write_grade || null,
            user_can_write: foundBoard.user_can_write, // API에서 받은 값 그대로 사용
          });
          console.log(
            "✅ [BoardContent] 게시판 정보 로드 완료:",
            foundBoard.name,
            "- user_can_write:",
            foundBoard.user_can_write
          );
        } else {
          console.warn("⚠️ [BoardContent] 게시판을 찾을 수 없음:", boardId);
          setBoardData(defaultBoardData[currentBoard] || defaultBoardData.free);
        }
      } else {
        console.warn(
          "⚠️ [BoardContent] 게시판 정보 로드 실패, 기본값 사용:",
          result.message
        );
        setBoardData(defaultBoardData[currentBoard] || defaultBoardData.free);
      }
    } catch (error) {
      console.error("❌ [BoardContent] 게시판 정보 로드 실패:", error);
      setBoardData(defaultBoardData[currentBoard] || defaultBoardData.free);
    }
  };

  // 게시글 목록 불러오기
  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      const boardServer = getServer("board-api-server");
      if (!boardServer || !boardServer.isActive) {
        console.warn("⚠️ 게시판 서버가 비활성화됨");
        setPosts([]);
        setTotalPosts(0);
        setTotalPages(1);
        setError("게시판 서버에 연결할 수 없습니다.");
        return;
      }

      const boardId = getBoardId(currentBoard);
      console.log("🚀 게시글 목록 조회 시작:", { currentBoard, boardId });

      // URL 파라미터 구성
      const params = new URLSearchParams();
      params.append("page", currentPage.toString());
      params.append("limit", PAGINATION_DEFAULTS.limit.toString());
      params.append("sort", sortBy);
      params.append("order", sortOrder);
      if (searchQuery) params.append("search", searchQuery);
      if (isSolved !== null) params.append("is_solved", isSolved.toString());

      // 프록시를 통한 API 호출
      const apiUrl = `/board-api/api/v1/boards/${boardId}/posts?${params.toString()}`;
      console.log("📞 [BoardContent] API 호출:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        // 권한 에러인 경우 응답 본문에서 메시지 추출
        let errorMessage = `API 호출 실패: ${response.status} ${response.statusText}`;

        try {
          const errorData = await response.json();
          console.log("📥 [BoardContent] 에러 응답:", errorData);

          // message 필드를 우선적으로 사용
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error && errorData.error.message) {
            errorMessage = errorData.error.message;
          }
        } catch (parseError) {
          // JSON 파싱 실패 시 기본 에러 메시지 사용
          console.warn(
            "⚠️ [BoardContent] 에러 응답 JSON 파싱 실패:",
            parseError
          );
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const { posts: fetchedPosts, pagination } = result.data;

        if (Array.isArray(fetchedPosts)) {
          setPosts(
            fetchedPosts.map((post) => ({
              id: post.id,
              title: post.title,
              author: post.author_name || post.author || "익명",
              date: post.created_at || post.date,
              views: post.views || 0,
              likes: post.likes || 0,
              replies: post.comments_count || post.replies || 0,
              solved: post.is_solved || false,
              content: post.content,
              board_id: post.board_id,
            }))
          );

          if (pagination) {
            setTotalPosts(pagination.total || 0);
            setTotalPages(pagination.total_pages || 1);
          }
        } else {
          setPosts([]);
          setTotalPosts(0);
          setTotalPages(1);
        }

        console.log(
          "✅ 게시글 목록 로드 완료:",
          fetchedPosts?.length || 0,
          "개"
        );
      } else {
        setPosts([]);
        setTotalPosts(0);
        setTotalPages(1);
        setError("게시글 데이터를 가져올 수 없습니다.");
      }
    } catch (error) {
      console.error("❌ 게시글 목록 로드 실패:", error);

      // 에러 메시지 설정 (이미 위에서 서버 메시지를 처리함)
      setError(error.message || "게시글 목록을 불러올 수 없습니다.");
      setPosts([]);
      setTotalPosts(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // 게시판 변경 시 데이터 다시 불러오기
  useEffect(() => {
    console.log("🔍 [BoardContent] 게시판 변경 useEffect:", {
      currentBoard,
      isConfigLoaded,
    });
    if (isConfigLoaded) {
      console.log("📋 [BoardContent] 데이터 로드 시작...");
      fetchBoardInfo();
      setCurrentPage(1);
      setSearchQuery(""); // 게시판 이동 시 검색창 초기화
      fetchPosts();
    }
  }, [currentBoard, isConfigLoaded]);

  // 페이지, 정렬, 검색 조건 변경 시 게시글 다시 불러오기
  useEffect(() => {
    if (isConfigLoaded && boardData) {
      fetchPosts();
    }
  }, [currentPage, sortBy, sortOrder, searchQuery, isSolved, isConfigLoaded]);

  // 검색 처리
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPosts();
  };

  // 정렬 변경
  const handleSortChange = (e) => {
    const selectedSort = POST_SORT_OPTIONS.find(
      (opt) => opt.value === e.target.value
    );
    if (selectedSort) {
      setSortBy(selectedSort.value);
      setCurrentPage(1);
    }
  };

  // 게시글 클릭 처리 - 부모 컴포넌트로 전달
  const handlePostClick = (post) => {
    console.log("🔍 [BoardContent] 게시글 클릭:", post.id);
    // 부모 컴포넌트(BoardPage)의 onPostClick 함수 호출
    if (onPostClick) {
      onPostClick(post);
    }
  };

  const currentBoardData =
    boardData || defaultBoardData[currentBoard] || defaultBoardData.free;

  return (
    <div className="board-content">
      {/* 모바일 헤더 */}
      <div className="mobile-header">
        <button className="sidebar-toggle" onClick={onToggleSidebar}>
          ☰
        </button>
        <h1>{currentBoardData.title}</h1>
        <div></div>
      </div>

      {/* 게시판 헤더 */}
      <div className="board-header">
        <div className="board-title">
          <span className="board-icon">{currentBoardData.icon}</span>
          <div>
            <h1>{currentBoardData.title}</h1>
            <p>{currentBoardData.description}</p>
          </div>
        </div>
        {(() => {
          const hasAuth = isJWTAuthenticated;
          const canWrite = currentBoardData.user_can_write;

          console.log('🔍 [Header Button] 권한 체크:', {
            hasAuth,
            canWrite,
            user_can_write: currentBoardData.user_can_write,
            boardTitle: currentBoardData.title
          });

          if (!hasAuth) {
            return (
              <button
                className="write-btn login-required"
                onClick={() => {
                  alert("게시글 작성은 로그인 후 이용 가능합니다.");
                }}
              >
                로그인 후 글쓰기
              </button>
            );
          } else if (canWrite !== true) {
            return (
              <button
                className="write-btn permission-denied"
                onClick={() => {
                  alert("이 게시판에 글을 작성할 권한이 없습니다.");
                }}
                disabled
              >
                권한 부족
              </button>
            );
          } else {
            return (
              <button className="write-btn" onClick={onWriteClick}>
                글쓰기
              </button>
            );
          }
        })()}
      </div>

      {/* 게시판 툴바 */}
      <div className="board-toolbar">
        <div className="toolbar-left">
          <select
            className="sort-select"
            value={sortBy}
            onChange={handleSortChange}
          >
            {POST_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <form className="search-box" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit">🔍</button>
          </form>
          {currentBoard === "qna" && (
            <select
              className="solved-filter"
              value={
                isSolved === null ? "all" : isSolved ? "solved" : "unsolved"
              }
              onChange={(e) => {
                const value = e.target.value;
                setIsSolved(value === "all" ? null : value === "solved");
                setCurrentPage(1);
              }}
            >
              <option value="all">전체</option>
              <option value="unsolved">미해결</option>
              <option value="solved">해결됨</option>
            </select>
          )}
        </div>
        <div className="toolbar-right">
          <span className="post-count">총 {totalPosts}개 글</span>
          {loading && <span className="loading-indicator">⏳</span>}
        </div>
      </div>

      {/* 오류 메시지 */}
      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      {/* 게시글 목록 */}
      <div className="post-list">
        {loading && posts.length === 0 ? (
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>게시글을 불러오는 중...</p>
          </div>
        ) : posts.length === 0 && !error ? (
          <div className="empty-message">
            <p>📝 게시글이 없습니다.</p>
            {searchQuery && (
              <p>검색어 '{searchQuery}'에 대한 결과가 없습니다.</p>
            )}
          </div>
        ) : posts.length > 0 ? (
          posts.map((post) => (
            <div
              key={post.id}
              className="post-item"
              onClick={() => handlePostClick(post)}
              style={{ cursor: "pointer" }}
            >
              <div className="post-main">
                <div className="post-title-area">
                  <h3 className="post-title">
                    {post.title}
                    {currentBoard === "qna" && (
                      <span
                        className={`solve-badge ${
                          post.solved ? "solved" : "unsolved"
                        }`}
                      >
                        {post.solved ? "해결됨" : "미해결"}
                      </span>
                    )}
                  </h3>
                  <div className="post-meta">
                    <span className="post-author">{post.author}</span>
                    <span className="post-date">
                      {formatDateTime(post.date)}
                    </span>
                  </div>
                </div>
                <div className="post-stats">
                  <span className="stat-item">
                    <span className="stat-icon">👁️</span>
                    {post.views}
                  </span>
                  <span className="stat-item">
                    <span className="stat-icon">👍</span>
                    {post.likes}
                  </span>
                  <span className="stat-item">
                    <span className="stat-icon">💬</span>
                    {post.replies}
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : null}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            ‹
          </button>

          {Array.from(
            {
              length: Math.min(PAGINATION_DEFAULTS.maxPageButtons, totalPages),
            },
            (_, i) => {
              const startPage = Math.max(
                1,
                currentPage - Math.floor(PAGINATION_DEFAULTS.maxPageButtons / 2)
              );
              const pageNum = startPage + i;

              if (pageNum > totalPages) return null;

              return (
                <button
                  key={pageNum}
                  className={`page-btn ${
                    currentPage === pageNum ? "active" : ""
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            }
          )}

          <button
            className="page-btn"
            onClick={() =>
              setCurrentPage(Math.min(totalPages, currentPage + 1))
            }
            disabled={currentPage === totalPages}
          >
            ›
          </button>
        </div>
      )}

      {/* 모바일 FAB 글쓰기 버튼 */}
      {(() => {
        const hasAuth = isJWTAuthenticated;
        const canWrite = currentBoardData.user_can_write;

        console.log('🔍 [FAB Button] 권한 체크:', {
          hasAuth,
          canWrite,
          user_can_write: currentBoardData.user_can_write,
          boardData: currentBoardData
        });

        // 로그인 상태이고 쓰기 권한이 명시적으로 true일 때만 FAB 버튼 표시
        if (hasAuth && canWrite === true) {
          return (
            <button
              className="fab-write-btn"
              onClick={onWriteClick}
              title="글쓰기"
            >
              ✏️
            </button>
          );
        }

        // 권한이 없으면 버튼 자체를 표시하지 않음
        return null;
      })()}
    </div>
  );
};

export default BoardContent;
